import React, { useEffect, useState } from "react";
import BASE_URL from "components/BaseUrl/apiConfig";

export default function UserMessageCountsChart({years = []}) {
  const [monthlyMessageCounts, setMonthlyMessageCounts] = useState([]);
  const [totalMessageCounts, setTotalMessageCounts] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [noData, setNoData] = useState(false);
  const storedUserData = localStorage.getItem("userData");
  let groupId = "";

  if (storedUserData) {
    const userData = JSON.parse(storedUserData);
    groupId = userData.groupId;
  }

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/Analytics/GetUserMessageCountsForChart?groupId=${groupId}&year=${selectedYear}`
        );
        if (!response.ok) {
          if (response.status === 404) {
            // Handle the no data found case
            setNoData(true);
            setMonthlyMessageCounts([]);
            setTotalMessageCounts([]);
            return;
          }
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setNoData(false);
        setMonthlyMessageCounts(data.monthlyMessageCounts || []);
        setTotalMessageCounts(data.totalMessageCounts || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setNoData(true);
        setMonthlyMessageCounts([]);
        setTotalMessageCounts([]);
      }
    };

    fetchChartData();
  }, [selectedYear]);

  const getMonthName = (monthNumber) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return monthNames[monthNumber - 1];
  };

  const transposedData = monthlyMessageCounts.reduce(
    (acc, { FullName, Month, MonthlyMessageCount }) => {
      if (!acc[FullName]) acc[FullName] = {};
      acc[FullName][Month] = MonthlyMessageCount;
      return acc;
    },
    {}
  );

  const totals = totalMessageCounts.reduce(
    (acc, { FullName, TotalMessages }) => {
      acc[FullName] = TotalMessages;
      return acc;
    },
    {}
  );

  const totalForEachMonth = Array.from({ length: 12 }, (_, i) => i + 1).reduce(
    (acc, month) => {
      acc[month] = Object.keys(transposedData).reduce((sum, userName) => {
        return sum + (transposedData[userName][month] || 0);
      }, 0);
      return acc;
    },
    {}
  );

  const capitalizeFirstLetter = (name) =>
    name.charAt(0).toUpperCase() + name.slice(1);

  return (
    <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
      <div className="rounded mr-4 pl-4 mt-4 bg-transparent">
        <div className="flex flex-wrap items-center">
          <div className="relative w-full max-w-full flex-grow flex-1">
            <div className="flex justify-between">
              <div>
                <h2
                  className="text-blueGray-700 text-xl font-semibold"
                  style={{ fontSize: "1rem" }}
                >
                  User's Messages Count
                </h2>
              </div>
              <div className="mr-4">
                <select
                  className="bg-white text-black border border-gray-300 rounded px-6 py-1 outline-none focus:outline-none w-32"
                  value={selectedYear}
                  onChange={(e) =>
                    setSelectedYear(parseInt(e.target.value, 10))
                  }
                >
                   {[...years]
                      .sort((a, b) => b - a) // descending
                      .map((yr) => (
                        <option key={yr} value={yr}>
                          {yr}
                        </option>
                      ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 flex-auto">
        <div className="relative h-auto">
          <div className="relative overflow-x-auto">
            {noData ? (
              <p className="text-center text-gray-500">
                No data found for this year
              </p>
            ) : (
              <table className="w-full min-w-full border border-gray-300">
                <thead>
                  <tr>
                    <th style={{ paddingLeft: "5px" }}>User Name</th>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(
                      (month) => (
                        <th key={month} style={{ paddingLeft: "5px" }}>
                          {getMonthName(month)}
                        </th>
                      )
                    )}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody style={{ paddingLeft: "5px" }}>
                  {Object.keys(transposedData).length > 0 ? (
                    Object.keys(transposedData).map((userName) => (
                      <tr key={userName} style={{ paddingLeft: "5px" }}>
                        <td style={{ paddingLeft: "5px" }}>
                          {capitalizeFirstLetter(userName)}
                        </td>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(
                          (month) => (
                            <td key={month} style={{ paddingLeft: "5px" }}>
                              {transposedData[userName][month] || 0}
                            </td>
                          )
                        )}
                        <td
                          style={{
                            backgroundColor: "paleturquoise",
                            fontWeight: "bold",
                          }}
                        >
                          {totals[userName] || 0}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={14} className="text-center text-gray-500">
                        No data available for this year
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td
                      style={{
                        fontWeight: "bold",
                        paddingLeft: "5px",
                      }}
                    >
                      Total Messages
                    </td>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(
                      (month) => (
                        <td
                          key={month}
                          style={{
                            backgroundColor: "rgba(16, 185, 129)",
                            color: "white",
                            fontWeight: "bold",
                            paddingLeft: "5px",
                          }}
                        >
                          {totalForEachMonth[month] || 0}
                        </td>
                      )
                    )}
                    <td
                      style={{
                        backgroundColor: "rgba(16, 185, 129)",
                        fontWeight: "bold",
                        color: "white",
                      }}
                    >
                      {Object.values(totalForEachMonth).reduce(
                        (sum, val) => sum + val,
                        0
                      )}
                    </td>
                  </tr>
                </tbody>
                <style>
                  {`
                    table, td, th {
                      border: 1px solid #ddd;
                      text-align: left; 
                      padding-left:5px;
                    }

                    table {
                      border-collapse: collapse;
                      width: 100%;
                    }

                    th {
                      background-color: #007bff;
                      color: white;
                      padding-left:5px;
                    }

                    tr:nth-child(even) {
                      background-color: #f2f2f2;
                    }

                    tr:hover {
                      background-color: #ddd;
                    }
                  `}
                </style>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
